import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { 
  Bell, AlertTriangle, Users, Calendar, TrendingUp, 
  CheckCircle2, Info, Clock, Target, AlertCircle,
  Activity, Shield, Database, DollarSign, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  burnout: AlertTriangle,
  staffing: Users,
  coverage: Calendar,
  hiring: TrendingUp,
  productivity: Target,
  compliance: CheckCircle2,
  system: Info,
  prn_alert: AlertTriangle
};

const CATEGORY_COLORS = {
  burnout: 'text-red-500 bg-red-50 dark:bg-red-900/20',
  staffing: 'text-amber-500 bg-amber-50 dark:bg-amber-900/20',
  coverage: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20',
  hiring: 'text-violet-500 bg-violet-50 dark:bg-violet-900/20',
  productivity: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20',
  compliance: 'text-[#00b0ab] bg-[#00b0ab]/10 dark:bg-indigo-900/20',
  system: 'text-slate-500 bg-slate-50 dark:bg-slate-800',
  prn_alert: 'text-amber-500 bg-amber-50 dark:bg-amber-900/20'
};

const TYPE_STYLES = {
  alert: 'border-l-red-500',
  warning: 'border-l-amber-500',
  info: 'border-l-blue-500',
  success: 'border-l-emerald-500'
};

const ALERT_DOMAIN_ICONS = {
  data_consistency: Database,
  financial_viability: DollarSign,
  workforce_health: Users,
  compliance: Shield,
  system_performance: Zap,
  overall: Activity
};

export default function NotificationBell() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [alertSummary, setAlertSummary] = useState(null);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('notifications');

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchAlertSummary = useCallback(async () => {
    if (!token) return;
    
    try {
      const [summaryRes, recentRes] = await Promise.all([
        fetch(`${API_URL}/api/alerts/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/alerts/recent?hours=24`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (summaryRes.ok) {
        setAlertSummary(await summaryRes.json());
      }
      if (recentRes.ok) {
        const data = await recentRes.json();
        setRecentAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Failed to fetch alert summary:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    fetchAlertSummary();
    
    // Poll for new notifications every 60 seconds
    const interval = setInterval(() => {
      fetchNotifications();
      fetchAlertSummary();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchAlertSummary]);

  const handleMarkRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/mark-all-read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  // Total badge count includes both unread notifications and unacknowledged alerts
  const totalBadgeCount = unreadCount + (alertSummary?.unacknowledged || 0);
  const alertCount = alertSummary?.unacknowledged || 0;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative hover:bg-[#00b0ab]/100/10"
          data-testid="notification-bell-btn"
        >
          <Bell className="h-5 w-5" />
          {totalBadgeCount > 0 && (
            <span className={`absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center animate-pulse ${alertSummary?.last_24h?.critical > 0 ? 'bg-red-500' : 'bg-[#00b0ab]/100'}`}>
              {totalBadgeCount > 9 ? '9+' : totalBadgeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="end">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-[#00b0ab]" />
            <h3 className="font-semibold">Notifications</h3>
          </div>
          <div className="flex items-center gap-2">
            {alertCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {alertCount} alert{alertCount !== 1 ? 's' : ''}
              </Badge>
            )}
            {unreadCount > 0 && (
              <Badge variant="secondary" className="bg-indigo-100 dark:bg-indigo-900/30 text-[#00b0ab]">
                {unreadCount} new
              </Badge>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full grid grid-cols-2 p-1 h-auto bg-slate-100 dark:bg-slate-900 rounded-none">
            <TabsTrigger 
              value="notifications" 
              className="text-xs py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800"
              data-testid="notifications-tab"
            >
              Notifications {unreadCount > 0 && `(${unreadCount})`}
            </TabsTrigger>
            <TabsTrigger 
              value="alerts" 
              className="text-xs py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800"
              data-testid="system-alerts-tab"
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              System Alerts {alertCount > 0 && `(${alertCount})`}
            </TabsTrigger>
          </TabsList>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="m-0">
            {unreadCount > 0 && (
              <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-800">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleMarkAllRead}
                  className="text-xs text-[#00b0ab] hover:text-indigo-700 p-0 h-auto"
                  data-testid="mark-all-read-btn"
                >
                  Mark all read
                </Button>
              </div>
            )}
            <ScrollArea className="h-80">
              {loading && notifications.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin h-6 w-6 border-2 border-indigo-500 border-t-transparent rounded-full" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                  <Bell className="h-8 w-8 mb-2 opacity-50" />
                  <p className="text-sm">No notifications</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {notifications.map((notification) => {
                    const IconComponent = CATEGORY_ICONS[notification.category] || Info;
                    const colorClass = CATEGORY_COLORS[notification.category] || CATEGORY_COLORS.system;
                    const borderClass = TYPE_STYLES[notification.type] || TYPE_STYLES.info;
                    
                    return (
                      <div
                        key={notification.id}
                        className={`p-4 border-l-4 ${borderClass} ${
                          !notification.read ? 'bg-[#00b0ab]/10/50 dark:bg-indigo-900/10' : ''
                        } hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors cursor-pointer`}
                        onClick={() => !notification.read && handleMarkRead(notification.id)}
                        data-testid={`notification-item-${notification.id}`}
                      >
                        <div className="flex gap-3">
                          <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                            <IconComponent className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className={`font-medium text-sm ${!notification.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {notification.title}
                              </p>
                              {!notification.read && (
                                <span className="h-2 w-2 rounded-full bg-[#00b0ab]/100 flex-shrink-0 mt-1.5" />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {notification.message}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                {notification.category}
                              </Badge>
                              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatTime(notification.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* System Alerts Tab */}
          <TabsContent value="alerts" className="m-0">
            <ScrollArea className="h-80">
              {/* Alert Summary */}
              {alertSummary && (
                <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 rounded-lg bg-white dark:bg-slate-800">
                      <div className="text-lg font-bold text-red-600">{alertSummary.last_24h?.critical || 0}</div>
                      <div className="text-[10px] text-muted-foreground">Critical</div>
                    </div>
                    <div className="p-2 rounded-lg bg-white dark:bg-slate-800">
                      <div className="text-lg font-bold text-amber-600">{alertSummary.last_24h?.warning || 0}</div>
                      <div className="text-[10px] text-muted-foreground">Warning</div>
                    </div>
                    <div className="p-2 rounded-lg bg-white dark:bg-slate-800">
                      <div className="text-lg font-bold text-blue-600">{alertSummary.unacknowledged || 0}</div>
                      <div className="text-[10px] text-muted-foreground">Pending</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Alerts List */}
              {recentAlerts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                  <CheckCircle2 className="h-8 w-8 mb-2 text-emerald-500" />
                  <p className="text-sm font-medium">All systems healthy</p>
                  <p className="text-xs">No alerts in the past 24 hours</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {recentAlerts.slice(0, 5).map((alert, idx) => {
                    const DomainIcon = ALERT_DOMAIN_ICONS[alert.domain] || Activity;
                    const isCritical = alert.severity === 'critical';
                    
                    return (
                      <div
                        key={idx}
                        className={`p-4 border-l-4 ${isCritical ? 'border-l-red-500 bg-red-50/50 dark:bg-red-900/10' : 'border-l-amber-500 bg-amber-50/50 dark:bg-amber-900/10'}`}
                        data-testid={`alert-item-${idx}`}
                      >
                        <div className="flex gap-3">
                          <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isCritical ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                            <DomainIcon className={`h-4 w-4 ${isCritical ? 'text-red-600' : 'text-amber-600'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge className={`text-[10px] ${isCritical ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                                {alert.severity}
                              </Badge>
                              <span className="text-xs font-medium capitalize">{alert.domain.replace(/_/g, ' ')}</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{alert.message}</p>
                            <div className="flex items-center gap-2 mt-2 text-[10px] text-muted-foreground">
                              <span>Score: {Math.round(alert.score)}%</span>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatTime(alert.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
        
        {/* Footer */}
        <div className="p-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full text-[#00b0ab] hover:text-indigo-700 hover:bg-[#00b0ab]/10 dark:hover:bg-indigo-900/20"
            onClick={() => {
              setIsOpen(false);
              navigate(activeTab === 'alerts' ? '/alerts' : '/pto-calendar');
            }}
            data-testid="view-all-btn"
          >
            {activeTab === 'alerts' ? 'View Alerts Dashboard' : 'View PTO Calendar'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
